import DOMPurify from 'dompurify';

/**
 * Security utilities for preventing XSS and other attacks
 */

// Configure DOMPurify with safe options for children's content
const purifyConfig = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'blockquote', 'img', 'a', 'span', 'div'
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'class', 'id', 'style'
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover'],
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  SANITIZE_DOM: true,
  USE_PROFILES: { html: true }
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Safe for displaying user-generated content
 */
export const sanitizeHTML = (htmlString: string): string => {
  if (!htmlString) return '';
  return DOMPurify.sanitize(htmlString, purifyConfig);
};

/**
 * Sanitizes HTML content for story content specifically
 * More permissive for rich text editor content
 */
export const sanitizeStoryContent = (htmlString: string): string => {
  if (!htmlString) return '';
  
  const storyConfig = {
    ...purifyConfig,
    ALLOWED_TAGS: [
      ...purifyConfig.ALLOWED_TAGS,
      'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'code', 'pre', 'mark', 'small', 'sub', 'sup'
    ],
    ALLOWED_ATTR: [
      ...purifyConfig.ALLOWED_ATTR,
      'colspan', 'rowspan', 'align'
    ]
  };
  
  return DOMPurify.sanitize(htmlString, storyConfig);
};

/**
 * Escapes HTML special characters for display as plain text
 */
export const escapeHTML = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Validates if a URL is safe for external links
 */
export const isValidURL = (url: string): boolean => {
  try {
    const parsedURL = new URL(url);
    // Only allow http and https protocols
    return ['http:', 'https:'].includes(parsedURL.protocol);
  } catch {
    return false;
  }
};

/**
 * Sanitizes and validates user input for story titles and names
 */
export const sanitizeUserInput = (input: string, maxLength: number = 200): string => {
  if (!input) return '';
  
  // Remove HTML tags and trim
  let sanitized = input.replace(/<[^>]*>/g, '').trim();
  
  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength).trim();
  }
  
  // Remove potentially dangerous characters
  sanitized = sanitized.replace(/[<>"'&]/g, '');
  
  return sanitized;
};

/**
 * Content Security Policy nonce generator for inline scripts
 * Use only when absolutely necessary
 */
export const generateCSPNonce = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, Array.from(array)));
};

/**
 * Secure way to set innerHTML with sanitization
 */
export const setSecureInnerHTML = (element: HTMLElement, htmlString: string): void => {
  element.innerHTML = sanitizeHTML(htmlString);
};

/**
 * Secure way to set story content with appropriate sanitization
 */
export const setSecureStoryHTML = (element: HTMLElement, htmlString: string): void => {
  element.innerHTML = sanitizeStoryContent(htmlString);
};

export default {
  sanitizeHTML,
  sanitizeStoryContent,
  escapeHTML,
  isValidURL,
  sanitizeUserInput,
  generateCSPNonce,
  setSecureInnerHTML,
  setSecureStoryHTML
};